import {doNormalizeURLForHttps} from '@Utils/url.util';
import type {QRCalendarDate, QRPayloadField, QRPayloadIssue, QRWallClock} from '@Libraries/qr-code/qr-code.types';
import {QRPayloadIssueCode} from '@Libraries/qr-code/qr-code.types';

const NON_DIGIT_PATTERN = /\D/g;
const WHITESPACE_PATTERN = /\s/g;
const RFC_3986_UNRESERVED_BY_ENCODE_URI_COMPONENT = /[!'()*]/g;
const LONE_SURROGATE_PATTERN = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;
const UNICODE_REPLACEMENT_CHARACTER = '�';
const HEX_RADIX = 16;

const ICAL_LINE_SEPARATOR = '\r\n';
const ICAL_FOLD_OCTET_LIMIT = 75;
const ICAL_FOLD_CONTINUATION_PREFIX = ' ';

const UTF8_TWO_OCTET_FLOOR = 0x80;
const UTF8_THREE_OCTET_FLOOR = 0x800;
const UTF8_FOUR_OCTET_FLOOR = 0x10000;

const FNV_1A_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_1A_PRIME = 0x100000001b3n;
const UNSIGNED_64_BIT_MASK = 0xffffffffffffffffn;
const CONTENT_HASH_HEX_LENGTH = 16;

const MS_PER_MINUTE = 60_000;

const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK_TIME_PATTERN = /^(\d{1,2}):(\d{2})$/;
const MIN_MONTH = 1;
const MAX_MONTH = 12;
const MIN_DAY_OF_MONTH = 1;
const MAX_DAY_OF_MONTH = 31;
const MAX_HOUR = 23;
const MAX_MINUTE = 59;

const textEncoder = new TextEncoder();

export const getUtf8ByteLength = (value: string): number => {
  return textEncoder.encode(value).length;
};

/**
 * A surrogate with no partner cannot be UTF-8 encoded, and `encodeURIComponent` throws `URIError` on one — reachable from a paste that cut an emoji in half. Substituting U+FFFD
 * matches what `TextEncoder` already does with the same input, so the byte count and the encoded value agree.
 */
const replaceLoneSurrogates = (value: string): string => {
  return value.replace(LONE_SURROGATE_PATTERN, UNICODE_REPLACEMENT_CHARACTER);
};

/**
 * `encodeURIComponent` leaves `!'()*` alone, so an apostrophe would travel literally inside a query string. Encoding them keeps the payload byte-identical across the tool, the
 * download and the poster item no matter which side composed it.
 */
export const encodeQueryValue = (value: string): string => {
  return encodeURIComponent(replaceLoneSurrogates(value)).replace(RFC_3986_UNRESERVED_BY_ENCODE_URI_COMPONENT, (character) => {
    return `%${character.charCodeAt(0).toString(HEX_RADIX).toUpperCase()}`;
  });
};

/**
 * Adds `https://` only when there is no scheme at all. Deliberately not `prependProtocol` from `@Utils/url.util`: that helper also infers `mailto:` for an address-shaped string
 * and `tel:` for a phone-shaped one, so a visitor typing their address into a link field would get a code that opens a mail client instead of a page.
 */
export const ensureHttpsProtocol = (link: string): string => {
  return doNormalizeURLForHttps(link.trim());
};

/** Strips spaces only — everything else the visitor typed travels as-is, per the field spec. */
export const normalisePhoneNumber = (phoneNumber: string): string => {
  return phoneNumber.replace(WHITESPACE_PATTERN, '');
};

/** `wa.me` carries the number as a path segment, where a `+` or punctuation breaks the link. */
export const getPhoneNumberDigits = (phoneNumber: string): string => {
  return phoneNumber.replace(NON_DIGIT_PATTERN, '');
};

export const escapeWifiValue = (value: string): string => {
  return value.replace(/([\\;,:"])/g, '\\$1');
};

/**
 * The TEXT value escaping shared by vCard (RFC 6350 §3.4) and iCalendar (RFC 5545 §3.3.11) — both reserve the same four sequences. Structural separators are added by the caller
 * after escaping, so a job title like `Director, Partnerships` cannot split the field it sits in.
 */
export const escapeStructuredTextValue = (value: string): string => {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
};

/**
 * Octets a code point occupies once UTF-8 encoded. A lone surrogate reports three, matching the U+FFFD that `TextEncoder` substitutes for it — so the fold stays exact for
 * malformed input too.
 */
const getCodePointOctetLength = (codePoint: number): number => {
  if (codePoint < UTF8_TWO_OCTET_FLOOR) {
    return 1;
  }
  if (codePoint < UTF8_THREE_OCTET_FLOOR) {
    return 2;
  }
  if (codePoint < UTF8_FOUR_OCTET_FLOOR) {
    return 3;
  }
  return 4;
};

/**
 * Trims a value to at most `maxBytes` once UTF-8 encoded. The cap is in bytes because that is what a QR code actually holds — an accent costs two and an emoji four, so a character
 * count would let a visitor past a limit they cannot see.
 */
export const clampToUtf8ByteLength = (value: string, maxBytes: number): string => {
  if (getUtf8ByteLength(value) <= maxBytes) {
    return value;
  }

  let clampedValue = '';
  let octets = 0;
  for (const character of value) {
    const characterOctets = getCodePointOctetLength(character.codePointAt(0) ?? 0);
    if (octets + characterOctets > maxBytes) {
      break;
    }
    clampedValue += character;
    octets += characterOctets;
  }

  return clampedValue;
};

/**
 * Folds a content line to 75 octets with a single-space continuation, per RFC 5545. Iterating code points means a multi-byte character is never split across the fold, and counting
 * octets from the code point rather than re-encoding keeps this off the per-keystroke hot path.
 */
const foldLine = (line: string): string => {
  const segments: string[] = [];
  let currentSegment = '';
  let currentOctets = 0;

  for (const character of line) {
    const characterOctets = getCodePointOctetLength(character.codePointAt(0) ?? 0);
    const octetLimit = segments.length === 0 ? ICAL_FOLD_OCTET_LIMIT : ICAL_FOLD_OCTET_LIMIT - ICAL_FOLD_CONTINUATION_PREFIX.length;
    if (currentOctets + characterOctets > octetLimit) {
      segments.push(currentSegment);
      currentSegment = character;
      currentOctets = characterOctets;
    } else {
      currentSegment += character;
      currentOctets += characterOctets;
    }
  }
  segments.push(currentSegment);

  return segments.join(`${ICAL_LINE_SEPARATOR}${ICAL_FOLD_CONTINUATION_PREFIX}`);
};

/** vCard and iCalendar both want CRLF-delimited, folded content lines and no trailing break. */
export const joinContentLines = (lines: string[]): string => {
  return lines.map(foldLine).join(ICAL_LINE_SEPARATOR);
};

/**
 * `null` rather than a partly-NaN structure for anything that is not a complete `YYYY-MM-DD`. A form is empty or half-typed most of the time it is on screen, and a NaN date
 * reaches `toISOString()` as a thrown `RangeError` — which the builders' never-throw contract forbids.
 */
const parseCalendarDate = (date: string): QRCalendarDate | null => {
  const match = CALENDAR_DATE_PATTERN.exec(date.trim());
  if (match === null) {
    return null;
  }

  const [, year, month, day] = match;
  const parsedMonth = Number(month);
  const parsedDay = Number(day);
  if (parsedMonth < MIN_MONTH || parsedMonth > MAX_MONTH || parsedDay < MIN_DAY_OF_MONTH || parsedDay > MAX_DAY_OF_MONTH) {
    return null;
  }
  return {year: Number(year), month: parsedMonth, day: parsedDay};
};

export const parseWallClock = (date: string, time: string): QRWallClock | null => {
  const calendarDate = parseCalendarDate(date);
  const match = WALL_CLOCK_TIME_PATTERN.exec(time.trim());
  if (calendarDate === null || match === null) {
    return null;
  }

  const [, hour, minute] = match;
  const parsedHour = Number(hour);
  const parsedMinute = Number(minute);
  if (parsedHour > MAX_HOUR || parsedMinute > MAX_MINUTE) {
    return null;
  }
  return {...calendarDate, hour: parsedHour, minute: parsedMinute};
};

export const formatUtcTimestamp = (date: Date): string => {
  return `${date.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
};

/**
 * The instant a wall-clock reading names, given how far ahead of UTC it was read. Plain arithmetic, so unlike resolving an IANA zone name there is nothing here that can throw on a
 * value the visitor typed.
 */
export const getUtcInstantForOffsetWallClock = (wallClock: QRWallClock, utcOffsetMinutes: number): Date => {
  return new Date(getFloatingDateTime(wallClock).getTime() - utcOffsetMinutes * MS_PER_MINUTE);
};

/**
 * A wall-clock reading with no zone attached. The `Date` is only a vehicle for the reading — it is not the instant the reading names, so never hand it to {@link
 * formatUtcTimestamp} directly.
 */
const getFloatingDateTime = (wallClock: QRWallClock): Date => {
  return new Date(Date.UTC(wallClock.year, wallClock.month - 1, wallClock.day, wallClock.hour, wallClock.minute));
};

/** FNV-1a over a string, as 16 hex characters. Stable across runs and platforms, not a security primitive — it only has to collide rarely. */
export const getContentHash = (content: string): string => {
  let hash = FNV_1A_OFFSET_BASIS;
  for (const byte of textEncoder.encode(content)) {
    hash = ((hash ^ BigInt(byte)) * FNV_1A_PRIME) & UNSIGNED_64_BIT_MASK;
  }
  return hash.toString(HEX_RADIX).padStart(CONTENT_HASH_HEX_LENGTH, '0');
};

const MINUTES_PER_HOUR = 60;
const OFFSET_LABEL_PREFIX = 'UTC';
const POSITIVE_SIGN = '+';
const NEGATIVE_SIGN = '−';
const ZERO_SIGN = '±';
const HOUR_MINUTE_SEPARATOR = ':';
const TWO_DIGITS = 2;
const DIGIT_PAD = '0';

/**
 * Every offset in real-world use, in minutes ahead of UTC — the three-quarter-hour ones (Nepal, Chatham, Eucla) included, since a list that quietly rounds them puts an event 45
 * minutes out. A fixed offset rather than an IANA zone name is a deliberate trade: it cannot fail to resolve, so the conversion is arithmetic that no browser can throw on.
 */
export const QR_UTC_OFFSET_MINUTES: number[] = [
  -720, -660, -600, -570, -540, -480, -420, -360, -300, -240, -210, -180, -120, -60, 0, 60, 120, 180, 210, 240, 270, 300, 330, 345, 360, 390, 420, 480, 525, 540, 570, 600, 630,
  660, 720, 765, 780, 840,
];

/** `UTC±00:00`, `UTC+05:30`, `UTC−04:00`. Not translated: it is a numeric format, not a sentence. */
export const getQRUtcOffsetLabel = (offsetMinutes: number): string => {
  const sign = offsetMinutes === 0 ? ZERO_SIGN : offsetMinutes > 0 ? POSITIVE_SIGN : NEGATIVE_SIGN;
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / MINUTES_PER_HOUR);
  const minutes = absoluteMinutes % MINUTES_PER_HOUR;

  return `${OFFSET_LABEL_PREFIX}${sign}${padOffsetPart(hours)}${HOUR_MINUTE_SEPARATOR}${padOffsetPart(minutes)}`;
};

const padOffsetPart = (part: number): string => {
  return String(part).padStart(TWO_DIGITS, DIGIT_PAD);
};

/**
 * The visitor's own offset at the given moment, which is right for the overwhelming majority of events — someone making a poster is almost always making it for where they are.
 * Falls back to UTC if the host's offset is not one this list carries, so the dropdown always opens on a value it can actually show.
 */
export const getQRDefaultUtcOffsetMinutes = (at: Date): number => {
  const hostOffsetMinutes = -at.getTimezoneOffset();
  return QR_UTC_OFFSET_MINUTES.includes(hostOffsetMinutes) ? hostOffsetMinutes : 0;
};

/**
 * What a bare domain becomes. https is the only sane default for something about to be printed: it is what practically every site serves, and a site that still answers on http
 * almost always redirects to https anyway. A visitor who genuinely needs http types it themselves and keeps it — see {@link parseQRUrl}.
 */
const DEFAULT_SCHEME = 'https';
/** The schemes a web-address code may carry. Anything else belongs to a different type's form. */
const WEB_SCHEMES: string[] = [DEFAULT_SCHEME, 'http'];

const ANY_WHITESPACE_PATTERN = /\s/;
/** RFC 3986 §3.1 scheme grammar, matched only as a prefix so the rest of the value is untouched. */
const SCHEME_PREFIX_PATTERN = /^([a-zA-Z][a-zA-Z0-9+.-]*):/;
const PATH_PREFIX = '/';
const ROOT_PATH = '/';
const AUTHORITY_PREFIX = '//';
const SCHEME_SEPARATOR = `:${AUTHORITY_PREFIX}`;

const DOMAIN_LABEL_SEPARATOR = '.';
const MIN_DOMAIN_LABELS = 2;
/** A label is alphanumeric with inner hyphens — which also accepts the `xn--` form of an IDN. */
const DOMAIN_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/** Letters only, so a bare IPv4 address and a typo like `site.c` are both turned away. */
const TOP_LEVEL_DOMAIN_PATTERN = /^[a-z]{2,}$/;

interface QRUrlParseResult {
  /** What to encode. Present even when `issues` is non-empty — falling back to the trimmed input — because the preview has to keep drawing while the visitor is still typing. */
  payload: string;
  issues: QRPayloadIssue[];
}

const toIssueResult = (payload: string, code: QRPayloadIssueCode, field: QRPayloadField): QRUrlParseResult => {
  return {payload, issues: [{code, field}]};
};

/**
 * A dot before the colon is a host with a port (`example.com:8080`), not a scheme — no scheme in practical use contains one, and hosts routinely do. Without this test that input
 * would be read as an unknown scheme and rejected.
 */
const getExplicitScheme = (link: string): string | null => {
  const schemeMatch = SCHEME_PREFIX_PATTERN.exec(link);
  if (schemeMatch === null) {
    return null;
  }
  const [, scheme] = schemeMatch;
  return scheme.includes(DOMAIN_LABEL_SEPARATOR) ? null : scheme.toLowerCase();
};

/**
 * A real registrable domain, which is what "a proper address" means for a code someone is about to print. `URL` has already lowercased and punycoded the host, so plain ASCII tests
 * cover an IDN too.
 */
const isProperDomainName = (hostname: string): boolean => {
  const labels = hostname.split(DOMAIN_LABEL_SEPARATOR);
  if (labels.length < MIN_DOMAIN_LABELS) {
    return false;
  }
  const hasOnlyValidLabels = labels.every((label) => {
    return DOMAIN_LABEL_PATTERN.test(label);
  });
  return hasOnlyValidLabels && TOP_LEVEL_DOMAIN_PATTERN.test(labels[labels.length - 1]);
};

/**
 * Rebuilt from the parsed parts rather than `toString()`, which would append a slash to every bare domain — a wasted byte on something whose density decides how small it can be
 * printed. Also drops the case of the scheme and host, so the same site typed two ways yields one code.
 */
const getEncodedUrl = (url: URL): string => {
  const hasPathOrQuery = url.pathname !== ROOT_PATH || url.search !== '' || url.hash !== '';
  const path = hasPathOrQuery ? url.pathname : '';
  return `${url.protocol}${AUTHORITY_PREFIX}${url.host}${path}${url.search}${url.hash}`;
};

/**
 * Turns whatever someone typed into a web address into the exact string a scanner should carry, plus whatever is wrong with it. Never throws: a half-typed value reads as
 * unfinished, not invalid. A scheme the visitor spelled out is kept verbatim, `http://` included — that is a decision about their own site, not an omission to correct.
 */
export const parseQRUrl = (link: string, field: QRPayloadField): QRUrlParseResult => {
  const trimmedLink = link.trim();
  if (trimmedLink === '') {
    return toIssueResult('', QRPayloadIssueCode.REQUIRED, field);
  }
  if (ANY_WHITESPACE_PATTERN.test(trimmedLink)) {
    return toIssueResult(trimmedLink, QRPayloadIssueCode.URL_HAS_WHITESPACE, field);
  }

  const explicitScheme = getExplicitScheme(trimmedLink);
  if (explicitScheme !== null && !WEB_SCHEMES.includes(explicitScheme)) {
    return toIssueResult(trimmedLink, QRPayloadIssueCode.URL_UNSUPPORTED_SCHEME, field);
  }
  // A value opening with a slash names a path on a site it never identifies, so there is nothing to
  // prepend a scheme to — `//example.com` would otherwise become `https:////example.com`.
  if (explicitScheme === null && trimmedLink.startsWith(PATH_PREFIX)) {
    return toIssueResult(trimmedLink, QRPayloadIssueCode.INVALID_URL, field);
  }

  const absoluteLink = explicitScheme === null ? `${DEFAULT_SCHEME}${SCHEME_SEPARATOR}${trimmedLink}` : trimmedLink;
  let url: URL;
  try {
    url = new URL(absoluteLink);
  } catch {
    return toIssueResult(trimmedLink, QRPayloadIssueCode.INVALID_URL, field);
  }

  if (url.username !== '' || url.password !== '') {
    return toIssueResult(trimmedLink, QRPayloadIssueCode.URL_HAS_CREDENTIALS, field);
  }
  if (!isProperDomainName(url.hostname)) {
    return toIssueResult(trimmedLink, QRPayloadIssueCode.INVALID_URL, field);
  }

  return {payload: getEncodedUrl(url), issues: []};
};
